/**
 * Modular logger utility with per-module log levels
 */

/**
 * Log levels in order of verbosity (lower number = more verbose)
 */
export enum LogLevel {
  DEBUG = 0,  // All logs including debug
  INFO = 1,   // log, info, warn, error (default)
  WARN = 2,   // warn, error
  ERROR = 3,  // Only errors
  NONE = 4    // No logs
}

/**
 * Logger instance for a specific module
 */
export class Logger {
  constructor(
    private moduleName: string,
    private level: LogLevel = LogLevel.INFO
  ) { }

  /**
   * Format message with module prefix
   */
  private format(message: string): string {
    return `[${this.moduleName}] ${message}`;
  }

  /**
   * Check if a log level should be output
   * In production, enforces a minimum level of WARN to prevent leaking
   * client IDs, document IDs, and other sensitive data via INFO/DEBUG logs.
   */
  private shouldLog(messageLevel: LogLevel): boolean {
    const effectiveLevel = import.meta.env.DEV ? this.level : Math.max(this.level, LogLevel.WARN);
    return messageLevel >= effectiveLevel;
  }

  /**
   * Output a log message with optional data using the specified console method
   */
  private output(
    consoleFn: (...args: any[]) => void,
    message: string,
    data?: any
  ): void {
    if (data !== undefined) {
      consoleFn(this.format(message), data);
    } else {
      consoleFn(this.format(message));
    }
  }

  /**
   * Debug level logging (most verbose)
   */
  debug(message: string, data?: any) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.output(console.log, message, data);
    }
  }

  /**
   * Standard logging
   */
  log(message: string, data?: any) {
    if (this.shouldLog(LogLevel.INFO)) {
      this.output(console.log, message, data);
    }
  }

  /**
   * Info level logging (alias for log)
   */
  info(message: string, data?: any) {
    this.log(message, data);
  }

  /**
   * Warning level logging
   */
  warn(message: string, data?: any) {
    if (this.shouldLog(LogLevel.WARN)) {
      this.output(console.warn, message, data);
    }
  }

  /**
   * Error level logging
   */
  error(message: string, data?: any) {
    if (this.shouldLog(LogLevel.ERROR)) {
      this.output(console.error, message, data);
    }
  }

  /**
   * Change the log level for this logger instance
   */
  setLevel(level: LogLevel) {
    this.level = level;
  }

  /**
   * Get the current log level
   */
  getLevel(): LogLevel {
    return this.level;
  }
}

/**
 * Create a logger for a specific module
 *
 * @param moduleName - Name of the module (will be prefixed to all logs)
 * @param level - Log level (defaults to LOG)
 *
 * @example
 * const logger = createLogger("MyModule", LogLevel.DEBUG);
 * logger.debug("Debug message", { foo: "bar" });
 * logger.log("Info message");
 * logger.warn("Warning message");
 * logger.error("Error message");
 */
export function createLogger(moduleName: string, level: LogLevel = LogLevel.INFO): Logger {
  return new Logger(moduleName, level);
}
